import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Visibility as VisibilityIcon } from "@mui/icons-material";
import { Chip, Grid, IconButton, Stack } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { PageContainer } from "@/features/ui";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useAuth } from "@/features/authentication";
import {
  customerClaimStatusColors,
  customerClaimStatusLabels,
} from "@/features/customer-claims/status";
import { CustomerSelect } from "@/features/customers";
import {
  AllOutletsSelect,
  OutletRoleEnum,
  UserOutletsSelect,
} from "@/features/outlets";
import { apiOutletClient, formatDate, getErrorMessage } from "@/features/shared";
import {
  CustomDateRangePicker,
  SearchTextField,
  useSnackbar,
} from "@/features/ui";
import {
  useDateRangeFilter,
  useSearchFilter,
  useServerPagination,
} from "@/hooks";

export const DeliveryCustomerClaims = () => {
  const { outlet, changeOutlet } = useAuth();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [customerClaims, setCustomerClaims] = useState([]);
  const [loading, setLoading] = useState(false);

  const {
    setRowCount,
    paginationParams,
    dataGridPaginationProps,
    resetParams,
  } = useServerPagination();
  const { keyword, searchProps } = useSearchFilter({ resetParams });
  const { dateRangeProps, dateParams } = useDateRangeFilter({ resetParams });

  const prevOutletRoleRef = useRef();

  // Parse buyer from URL params
  const buyer = useMemo(() => {
    const buyerParam = searchParams.get("buyer");
    if (!buyerParam) return null;
    try {
      return JSON.parse(buyerParam);
    } catch {
      return null;
    }
  }, [searchParams]);

  const loadCustomerClaims = useCallback(async () => {
    if (!outlet) return;

    setLoading(true);
    try {
      const {
        data: { data, pageInfo },
      } = await apiOutletClient.get("customer-claims", {
        params: {
          ...paginationParams,
          ...dateParams,
          ...(keyword && { referenceNumber: keyword }),
          buyerableId: buyer?.id || null,
          types: ["DELIVERY"],
        },
      });
      setCustomerClaims(data);
      setRowCount(pageInfo.totalItems);
    } catch (error) {
      showSnackbar(getErrorMessage(error, "Gagal memuat klaim kirim barang"));
    } finally {
      setLoading(false);
    }
  }, [
    outlet,
    paginationParams,
    dateParams,
    keyword,
    setRowCount,
    buyer,
    showSnackbar,
  ]);

  useEffect(() => {
    loadCustomerClaims();
  }, [loadCustomerClaims]);

  // Reset buyer when outlet role changes
  useEffect(() => {
    if (
      prevOutletRoleRef.current !== undefined &&
      prevOutletRoleRef.current !== outlet?.role
    ) {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.delete("buyer");
        return newParams;
      });
    }
    prevOutletRoleRef.current = outlet?.role;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outlet?.role]);

  const handleBuyerChange = (value) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      if (value) {
        newParams.set(
          "buyer",
          JSON.stringify({ id: value.id, name: value.name })
        );
      } else {
        newParams.delete("buyer");
      }
      resetParams.forEach((param) => newParams.delete(param));
      return newParams;
    });
  };

  return (
    <PageContainer breadcrumbs={[]} maxWidth={false}>
      <Stack spacing={3}>
        <Grid container spacing={2} sx={{ justifyContent: "flex-end", alignItems: "end" }}>
          <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
            {outlet?.role === OutletRoleEnum.HUB ? (
              <AllOutletsSelect
                value={buyer}
                onChange={handleBuyerChange}
                allowedRoles={[OutletRoleEnum.RETAIL]}
                TextFieldProps={{
                  label: "Pelanggan (Retail)",
                }}
              />
            ) : (
              <CustomerSelect
                value={buyer}
                onChange={handleBuyerChange}
                TextFieldProps={{
                  label: "Pelanggan (Customer)",
                }}
              />
            )}
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
            <UserOutletsSelect
              value={outlet}
              onChange={changeOutlet}
              TextFieldProps={{
                label: "Penjual",
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
            <SearchTextField
              fullWidth
              placeholder="Cari nomor referensi"
              {...searchProps}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 12, lg: 6 }}>
            <CustomDateRangePicker {...dateRangeProps} />
          </Grid>
        </Grid>
        <DataGrid
          rows={customerClaims}
          loading={loading}
          getRowHeight={() => "auto"}
          columns={[
            {
              field: "referenceNumber",
              headerName: "No. Klaim",
              flex: 2,
              sortable: false,
            },
            {
              field: "transactionDate",
              headerName: "Tanggal Klaim",
              flex: 2,
              sortable: false,
              valueFormatter: (value) => formatDate(value),
            },
            {
              field: "buyer",
              headerName: "Pelanggan",
              flex: 3,
              sortable: false,
              valueGetter: (buyer) => buyer.name || "-",
            },
            {
              field: "status",
              headerName: "Status",
              flex: 2,
              sortable: false,
              renderCell: ({ row }) => (
                <Chip
                  label={customerClaimStatusLabels[row.status]}
                  color={customerClaimStatusColors[row.status]}
                />
              ),
            },
            {
              field: "actions",
              type: "actions",
              headerName: "Aksi",
              getActions: ({ row }) => [
                <IconButton
                  key="view"
                  onClick={() => navigate(row.referenceNumber)}
                  size="small"
                >
                  <VisibilityIcon />
                </IconButton>,
              ],
            },
          ]}
          {...dataGridPaginationProps}
          disableRowSelectionOnClick
        />
      </Stack>
    </PageContainer>
  );
};
